import { useMemo, useState } from 'react';
import {
  useGetPtoRdGapsConfigQuery,
  useGetPtoRdGapsQuery,
  type PtoRdGapsConfig,
} from '@shared/api';
import { screenEstimates, type SpEstimate } from './logic';

/** Анализ комплектности (№7) на экране позиций ревизии: включён ли он для
 *  проекта и роли, значки «⚠ N» строк ВОР, оценки по нормам у строк СП (ЛКМ)
 *  и строка, со значка которой перешли на вкладку анализа. */
export interface RdGapsScreen {
  /** Флаг `OSNOVA_PTO_RD_GAPS`: `on` — всем, `dry` — пилотным проектам и только
   *  «Инженер+» и администраторам; выключен — вкладки и значков нет. */
  enabled: boolean;
  mode: PtoRdGapsConfig['mode'];
  /** «Сохранить как типовой комплект» — не ниже «Инженер+». */
  canSaveRules: boolean;
  /** record_uid строки ВОР → число находок; пусто — проверки ещё не было. */
  flags: Readonly<Record<string, number>>;
  /** record_uid строки СП → оценки по нормам (ЛКМ, который в СП есть). */
  estimates: Readonly<Record<string, SpEstimate[]>>;
  /** Строка ВОР со значка «⚠ N»: вкладка показывает только её находки. */
  rowUid: string | null;
  showRow: (recordUid: string) => void;
  clearRow: () => void;
}

const NO_FLAGS: Readonly<Record<string, number>> = Object.freeze({});
const NO_ESTIMATES: Readonly<Record<string, SpEstimate[]>> = Object.freeze({});

/** Настройка и значки анализа комплектности для экрана ревизии. Состояние
 *  находок (`…/rd-gaps`) — общий кеш с вкладкой: она опрашивает его, пока
 *  идёт прогон, значки обновляются вместе с ней. */
export function useRdGapsScreen(projectId: string, revisionId: string): RdGapsScreen {
  const { data: cfg } = useGetPtoRdGapsConfigQuery(projectId, { skip: !projectId });
  const enabled = cfg?.enabled === true;
  const { data } = useGetPtoRdGapsQuery(revisionId, { skip: !enabled || !revisionId });
  // Строка — своя у каждой ревизии (экран не пересоздаётся при переходе на
  // другую ревизию).
  const [row, setRow] = useState<{ revisionId: string; uid: string } | null>(null);
  const estimates = useMemo(() => (enabled ? screenEstimates(data) : NO_ESTIMATES), [enabled, data]);
  return {
    enabled,
    mode: cfg?.mode ?? 'off',
    canSaveRules: cfg?.can_save_rules === true,
    flags: (enabled && data?.row_flags) || NO_FLAGS,
    estimates,
    rowUid: row?.revisionId === revisionId ? row.uid : null,
    showRow: (uid) => setRow({ revisionId, uid }),
    clearRow: () => setRow(null),
  };
}
